
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FileText, Edit } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Program } from "@/types/program";
import { redirectToEditor } from "@/utils/redirectToEditor";

interface ProgramRowProps {
  program: Program;
  selected: boolean;
  onSelect: (id: string, e: React.MouseEvent<Element, MouseEvent>) => void;
  onClick: (id: string) => void;
}

export function ProgramRow({ 
  program, 
  selected, 
  onSelect,
  onClick
}: ProgramRowProps) {
  const navigate = useNavigate();

  const handleViewProgramInfo = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(program.id);
  };

  const handleEditProgram = (e: React.MouseEvent) => {
    e.stopPropagation();
    redirectToEditor(program.id);
  };

  const handleSelectCheckbox = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(program.id, e);
  };

  return (
    <TableRow 
      key={program.id}
      className="hover:bg-white/5 border-white/20 cursor-pointer"
      onClick={() => onClick(program.id)}
    >
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox 
          checked={selected}
          onClick={handleSelectCheckbox}
        />
      </TableCell>
      <TableCell>
        <div className="font-medium text-white">{program.name}</div>
        <div className="text-sm text-white/70">{program.description}</div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-2">
          {program.assignedClients.map((client) => (
            <div key={client.id} className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                {client.avatar && client.avatar !== "/placeholder.svg" && (
                  <AvatarImage src={client.avatar} alt={client.name} />
                )}
                <AvatarFallback>
                  {client.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-white">{client.name}</span>
            </div>
          ))}
        </div>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
          <Button 
            variant="default"
            className="w-full flex items-center justify-center"
            onClick={handleViewProgramInfo}
          >
            <FileText className="mr-2 h-4 w-4" />
            Program Info
          </Button>
          
          <Button 
            variant="purple"
            className="w-full flex items-center justify-center"
            onClick={handleEditProgram}
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit Program
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
